"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { captureUtm, getUtm } from "@/lib/utm/store";
import { withUtmParams } from "@/lib/utm/params";

const LINK_ACTIVATION_EVENTS = ["pointerdown", "click"] as const;

/**
 * Persists the landing UTMs for the session and adds them to any plain `<a>`
 * (portals, cross-brand links, CMS HTML) right before it is followed.
 */
export default function UtmTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    captureUtm(window.location.search);
  }, [pathname, searchParams]);

  useEffect(() => {
    function decorateLink(event: Event) {
      const utm = getUtm();
      const anchor =
        event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!utm || !(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href") ?? "";
      const decorated = withUtmParams(href, utm, window.location.href);
      if (decorated !== href) anchor.setAttribute("href", decorated);
    }

    // Window capture runs ahead of every document-level handler, including ones that stop propagation.
    for (const type of LINK_ACTIVATION_EVENTS) {
      window.addEventListener(type, decorateLink, { capture: true });
    }
    return () => {
      for (const type of LINK_ACTIVATION_EVENTS) {
        window.removeEventListener(type, decorateLink, { capture: true });
      }
    };
  }, []);

  return null;
}
